// 2026-07-23 极简 composer: useDraftForm — 单一 draft 状态的轻量 hook。
//
// 设计:
//   - 不调 API(由 caller 决定何时 upsert / send / delete)
//   - 不做 autosave(由 caller 在 close/send 时决定)
//   - 不做 dirty 检查的副作用(只暴露 isDirty 派生值)
//   - 不持有 useState 以外的状态
//
// 这是「Mail V2 单一 draft 状态」模型的最小实现:
//   currentDraft = 当前 Editor 看到的对象
//   lastSavedDraft = 上一次成功保存的快照(用于 isDirty 比对)
//   setField(key, value) = 改 currentDraft
//   isDirty = currentDraft !== lastSavedDraft
//
// useState 只用 2 个:currentDraft / lastSavedDraft。
// 整个文件不依赖 React 业务模块,只依赖 mailComposerTypes 的 type + utils。

import { useCallback, useMemo, useRef, useState } from "react";
import {
  createSnapshot,
  isSameSnapshot,
  EMPTY_FIELDS,
  type MailComposerFields,
  type MailComposerSnapshot,
} from "./mailComposerTypes";

export type MailDraftForm = MailComposerFields & {
  /** 已有 draft 时是后端返回的 id;新建邮件 (mode="new") 时为 null。 */
  id: string | null;
  /** 父级 message id — Reply/Forward 才有;新建 + 编辑独立 draft 时为 null。 */
  parentMessageId: string | null;
  /** 关联的 mail account id。 */
  mailAccountId: string;
};

export const EMPTY_DRAFT_FORM: Omit<MailDraftForm, "mailAccountId"> = {
  id: null,
  parentMessageId: null,
  ...EMPTY_FIELDS,
};

export function createEmptyDraftForm(mailAccountId: string): MailDraftForm {
  return { ...EMPTY_DRAFT_FORM, mailAccountId };
}

export interface UseDraftFormReturn {
  draft: MailDraftForm;
  /** 是否 dirty(字段与 lastSaved 不一致)。id / parentMessageId 变化不算 dirty(那些是「切换 draft」)。 */
  isDirty: boolean;
  /** 修改单个字段(对所有 MailComposerFields key 强类型)。 */
  setField: <K extends keyof MailComposerFields>(key: K, value: MailComposerFields[K]) => void;
  /** 整对象替换 — 用于外部 API 响应(GET /drafts/{id})回灌字段。
   *  同时把 lastSaved 一起重置 → isDirty 自动 false,避免「载入后立刻被认为 dirty」的反直觉行为。 */
  setDraft: (next: MailDraftForm) => void;
  /** 标记当前 draft 已成功保存(快照当前 draft,isDirty 自动变 false)。 */
  markSaved: () => void;
  /** 把整个 form 重置为新空白(用于「新建邮件」按钮)。同时清 lastSaved → isDirty=false。 */
  resetToEmpty: (mailAccountId: string) => void;
}

/**
 * useDraftForm — 极简 draft 状态管理。
 *
 * 故意不做:
 *   - autosave(debounce)
 *   - 上一次成功请求的 idempotency_key 缓存
 *   - 在内部调 API
 *
 * 调用方负责:
 *   - 监听 isDirty,决定何时调 upsertDraft
 *   - 在 close/send/delete 时,先调 API 再清空 / 关闭
 */
export function useDraftForm(initial: MailDraftForm): UseDraftFormReturn {
  const [draft, setDraftState] = useState<MailDraftForm>(initial);
  const [lastSaved, setLastSaved] = useState<MailComposerSnapshot>(createSnapshot(initial));
  // 用 ref 跟踪最新 draft 引用,让 markSaved 拿到 fresh 值(避免 stale closure)
  const draftRef = useRef<MailDraftForm>(initial);
  draftRef.current = draft;

  const setField = useCallback<UseDraftFormReturn["setField"]>((key, value) => {
    setDraftState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // 整对象替换:同时把 lastSaved 一起重置 → 避免「载入后立刻被认为 dirty」
  const setDraft = useCallback((next: MailDraftForm) => {
    setDraftState(next);
    setLastSaved(createSnapshot(next));
  }, []);

  // markSaved:用 ref 读取 fresh draft,不会 stale
  const markSaved = useCallback(() => {
    setLastSaved(createSnapshot(draftRef.current));
  }, []);

  // resetToEmpty:同时清 lastSaved → isDirty=false
  const resetToEmpty = useCallback((mailAccountId: string) => {
    const empty = createEmptyDraftForm(mailAccountId);
    setDraftState(empty);
    setLastSaved(createSnapshot(empty));
  }, []);

  const isDirty = useMemo(() => {
    const current = createSnapshot(draft);
    return !isSameSnapshot(current, lastSaved);
  }, [draft, lastSaved]);

  return { draft, isDirty, setField, setDraft, markSaved, resetToEmpty };
}
